import React, { useState } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { Confirm } from '../../../Commons/Modals/Confirm';
import { TogglePause, ResetDynamic } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Sección colapsable de controles críticos y zona de peligro para la dinámica "Arma la Playlist" (Tile ID 46).
 * Permite alternar la recepción de sugerencias de los aficionados y reiniciar la dinámica a ceros previa validación de PIN/contraseña.
 * 
 * @component PlaylistCriticalControls
 * @param {Object} props
 * @param {boolean} props.isSubmissionActive - Estado actual de recepción de sugerencias de aficionados.
 * @param {Function} [props.onTogglePause] - Callback opcional al cambiar estado de recepción.
 * @param {Function} [props.onResetSuccess] - Callback al purgar/reiniciar exitosamente la dinámica.
 */
export const PlaylistCriticalControls = ({
    isSubmissionActive = true,
    onTogglePause,
    onResetSuccess
}) => {
    const { showMessage } = useFloatMessage();

    // Estado del acordeón colapsable (inicia cerrado o abierto)
    const [isCollapsed, setIsCollapsed] = useState(true);

    // Estados para el reinicio y contraseña
    const [resetPin, setResetPin] = useState('');
    const [showPinInput, setShowPinInput] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [isTogglingPause, setIsTogglingPause] = useState(false);

    // Modal de confirmación Confirm.js
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        actionToYesButton: null,
        textYesButton: 'Sí, reiniciar',
        textNoButton: 'Cancelar'
    });

    // Alternar recepción de sugerencias de aficionados
    const handleToggleSubmission = async () => {
        setIsTogglingPause(true);
        const nextState = !isSubmissionActive;
        try {
            const res = await TogglePause(nextState);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextState
                        ? '🟢 Recepción de sugerencias ACTIVADA para los aficionados.'
                        : '⏸️ Recepción de sugerencias PAUSADA. El formulario de aficionados se encuentra cerrado.',
                    message: nextState
                        ? '🟢 Recepción de sugerencias ACTIVADA para los aficionados.'
                        : '⏸️ Recepción de sugerencias PAUSADA. El formulario de aficionados se encuentra cerrado.'
                });

                if (onTogglePause) {
                    onTogglePause(nextState);
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'No fue posible cambiar el estado de recepción.',
                    message: (res && res.message) || 'No fue posible cambiar el estado de recepción.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al alternar la recepción de sugerencias.',
                message: 'Error de red al alternar la recepción de sugerencias.'
            });
        } finally {
            setIsTogglingPause(false);
        }
    };

    // Iniciar flujo de reinicio / confirmación
    const handleInitiateReset = () => {
        if (!resetPin.trim()) {
            setShowPinInput(true);
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Ingresa la contraseña maestra o PIN de seguridad para proceder con el reinicio.',
                message: 'Ingresa la contraseña maestra o PIN de seguridad para proceder con el reinicio.'
            });
            return;
        }

        setConfirmConfig({
            message: '¿Estás completamente seguro de reiniciar la dinámica "Arma la Playlist"? Esta acción purgará todas las sugerencias, votos y la playlist activa del estadio de forma irreversible.',
            textYesButton: 'Sí, reiniciar dinámica',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                setIsResetting(true);
                try {
                    const res = await ResetDynamic(resetPin.trim());
                    if (res && res.success) {
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            text: '✅ Dinámica de Playlist reiniciada y reseteada a ceros exitosamente.',
                            message: '✅ Dinámica de Playlist reiniciada y reseteada a ceros exitosamente.'
                        });
                        setResetPin('');
                        setShowPinInput(false);
                        if (onResetSuccess) {
                            onResetSuccess();
                        }
                        return { success: true };
                    } else {
                        const errMsg = (res && res.message) || 'Contraseña o PIN incorrecto. No se realizó el reinicio.';
                        showMessage({
                            type: MESSAGE_TYPE.BAD,
                            text: errMsg,
                            message: errMsg
                        });
                        return { success: false, message: errMsg };
                    }
                } catch (error) {
                    const errMsg = 'Error de conexión al procesar el reinicio de la dinámica.';
                    showMessage({
                        type: MESSAGE_TYPE.BAD,
                        text: errMsg,
                        message: errMsg
                    });
                    return { success: false, message: errMsg };
                } finally {
                    setIsResetting(false);
                }
            }
        });

        setShowConfirmModal(true);
    };

    return (
        <section className="PlaylistCriticalControls" aria-label="Zona de controles críticos y reinicio">
            {/* Cabecera del Acordeón Colapsable */}
            <div
                className={`PlaylistCriticalControls__Header ${!isCollapsed ? 'PlaylistCriticalControls__Header--open' : ''}`}
                onClick={() => setIsCollapsed(prev => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsCollapsed(prev => !prev);
                    }
                }}
            >
                <div className="PlaylistCriticalControls__HeaderLeft">
                    <span className="PlaylistCriticalControls__HeaderIcon">
                        <i className="bi bi-shield-lock-fill"></i>
                    </span>
                    <div>
                        <h3 className="PlaylistCriticalControls__Title mb-0">
                            Controles Críticos y Zona de Peligro
                        </h3>
                        <p className="PlaylistCriticalControls__Subtitle mb-0">
                            Gestión de recepción de sugerencias en vivo y reinicio maestro
                        </p>
                    </div>
                </div>

                <div className="PlaylistCriticalControls__HeaderRight">
                    {/* Badge de Estatus de Sugerencias */}
                    <span
                        className={`PlaylistCriticalControls__StatusBadge ${
                            isSubmissionActive
                                ? 'PlaylistCriticalControls__StatusBadge--active'
                                : 'PlaylistCriticalControls__StatusBadge--paused'
                        }`}
                    >
                        <i className={`bi ${isSubmissionActive ? 'bi-broadcast' : 'bi-pause-circle-fill'} me-1`}></i>
                        {isSubmissionActive ? 'Recepción Abierta' : 'Recepción Pausada'}
                    </span>

                    {/* Botón Indicador de Acordeón */}
                    <button
                        type="button"
                        className="PlaylistCriticalControls__ToggleBtn"
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expandir controles críticos' : 'Colapsar controles críticos'}
                    >
                        <i className={`bi ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
                    </button>
                </div>
            </div>

            {/* Cuerpo del Acordeón */}
            {!isCollapsed && (
                <div className="PlaylistCriticalControls__Body">
                    <div className="row g-3 align-items-stretch">
                        {/* Bloque 1: Pausar / Reanudar Sugerencias de Aficionados */}
                        <div className="col-12 col-lg-6">
                            <div className="PlaylistCriticalControls__Panel PlaylistCriticalControls__Panel--pause">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className={`bi ${isSubmissionActive ? 'bi-soundwave text-success' : 'bi-slash-circle-fill text-warning'} fs-4`}></i>
                                        <div>
                                            <h4 className="PlaylistCriticalControls__PanelTitle mb-0">
                                                Recepción de Sugerencias
                                            </h4>
                                            <span className="PlaylistCriticalControls__PanelSubtitle">
                                                Formulario móvil para los aficionados
                                            </span>
                                        </div>
                                    </div>

                                    <span
                                        className={`badge ${
                                            isSubmissionActive ? 'bg-success' : 'bg-warning text-dark'
                                        } px-2 py-1 fw-bold`}
                                    >
                                        {isSubmissionActive ? '🟢 Activa' : '⏸️ Pausada'}
                                    </span>
                                </div>

                                <p className="PlaylistCriticalControls__PanelDesc">
                                    {isSubmissionActive
                                        ? 'Los aficionados en el estadio pueden enviar sugerencias y emitir votos en tiempo real.'
                                        : 'La recepción de canciones se encuentra bloqueada. Los aficionados verán el mensaje de cierre temporal.'}
                                </p>

                                <div className="mt-auto pt-2">
                                    <button
                                        type="button"
                                        className={`btn w-100 fw-bold d-flex align-items-center justify-content-center gap-2 py-2 ${
                                            isSubmissionActive ? 'btn-outline-warning' : 'btn-success'
                                        }`}
                                        onClick={handleToggleSubmission}
                                        disabled={isTogglingPause}
                                    >
                                        {isTogglingPause ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                <span>Actualizando recepción...</span>
                                            </>
                                        ) : isSubmissionActive ? (
                                            <>
                                                <i className="bi bi-pause-circle-fill fs-5"></i>
                                                <span>Pausar Recepción de Aficionados</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-play-circle-fill fs-5"></i>
                                                <span>Reanudar Recepción de Aficionados</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Bloque 2: Zona de Peligro / Reinicio de Dinámica a Ceros */}
                        <div className="col-12 col-lg-6">
                            <div className="PlaylistCriticalControls__Panel PlaylistCriticalControls__Panel--danger">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-exclamation-octagon-fill text-danger fs-4"></i>
                                        <div>
                                            <h4 className="PlaylistCriticalControls__PanelTitle text-danger mb-0">
                                                Zona de Peligro • Reinicio
                                            </h4>
                                            <span className="PlaylistCriticalControls__PanelSubtitle">
                                                Reseteo maestro a ceros
                                            </span>
                                        </div>
                                    </div>
                                    <span className="badge bg-danger text-white px-2 py-1 fw-bold">
                                        Irreversible
                                    </span>
                                </div>

                                <p className="PlaylistCriticalControls__PanelDesc">
                                    Purgará todas las sugerencias recibidas, votos registrados y vaciará la playlist activa del estadio para iniciar un nuevo partido.
                                </p>

                                <div className="PlaylistCriticalControls__ResetBox mt-auto pt-2">
                                    {showPinInput ? (
                                        <div className="d-flex flex-column gap-2">
                                            <div className="input-group">
                                                <span className="input-group-text bg-dark border-danger text-danger">
                                                    <i className="bi bi-key-fill"></i>
                                                </span>
                                                <input
                                                    type="password"
                                                    className="form-control bg-dark text-white border-danger"
                                                    placeholder="Ingresa PIN o contraseña maestra"
                                                    value={resetPin}
                                                    onChange={(e) => setResetPin(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleInitiateReset();
                                                        }
                                                    }}
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-danger fw-bold px-3"
                                                    onClick={handleInitiateReset}
                                                    disabled={isResetting || !resetPin.trim()}
                                                >
                                                    {isResetting ? (
                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    ) : (
                                                        'Confirmar'
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        setShowPinInput(false);
                                                        setResetPin('');
                                                    }}
                                                    title="Cancelar"
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            </div>
                                            <span className="text-danger small" style={{ fontSize: '11px' }}>
                                                <i className="bi bi-info-circle me-1"></i>
                                                Requiere confirmación antes de la purga final.
                                            </span>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger w-100 fw-bold d-flex align-items-center justify-content-center gap-2 py-2"
                                            onClick={() => setShowPinInput(true)}
                                        >
                                            <i className="bi bi-trash-fill fs-5"></i>
                                            <span>Reiniciar Dinámica (Solicitar PIN)</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmación Confirm.js */}
            {showConfirmModal && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.actionToYesButton}
                    textYesButton={confirmConfig.textYesButton}
                    textNoButton={confirmConfig.textNoButton}
                    setShowModal={setShowConfirmModal}
                />
            )}
        </section>
    );
};

export default PlaylistCriticalControls;
